import { useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faFileLines,
  faImage,
  faMagnifyingGlass,
  faCopy,
} from '@fortawesome/pro-regular-svg-icons';
import { Input } from '@design-system/components/input/input';
import { Button } from '@design-system/components/button/button';
import { Typography } from '@design-system/components/typography/typography';
import { ProfileVM } from '../view-model/Profile.viewModel';
import { ProfileSection } from './components/ProfileSection';
import { IdentitySection } from './components/IdentitySection';
import { StorySection } from './components/StorySection';
import { MediaSection } from './components/MediaSection';
import { DiscoverySection } from './components/DiscoverySection';
import { AiAssistModal } from './components/AiAssistModal';
import { Preview } from '../MobilePreview';
import { rgb } from '@design-system/tokens';
import toast from 'react-hot-toast';
import { useDebounced } from '@client/new/hooks/useDebounced';
import * as styles from './Profile.view.css';

const PREVIEW_DEBOUNCE_MS = 300;

export const ProfileView = (p: ProfileVM) => {
  const { completions, aiAssist, formValues } = p;
  const debouncedProfile = useDebounced(formValues, PREVIEW_DEBOUNCE_MS);

  const handleCopyUrl = useCallback(async () => {
    const fullUrl = `https://my.playbookapp.io/${p.customUrl.value}`;

    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Failed to copy URL');
    }
  }, [p.customUrl.value]);

  const handleAiAccept = useCallback(() => {
    const result = aiAssist.accept();
    if (!result) return;

    p.form.setValue(result.formField, result.text, { shouldDirty: true });
  }, [aiAssist, p.form]);

  return (
    <div className={styles.pageContainer}>
      <div className={styles.pageHeader}>
        <div className={styles.headerInner}>
          <div className={styles.headerLeft}>
            <h1 className={styles.pageTitle}>Your Profile</h1>
            <span className={styles.statusBadge({ live: p.isLive })}>
              {p.isLive ? 'Live' : 'Not live yet'}
            </span>
          </div>
          <div className={styles.headerRight}>
            {p.form.isDirty && (
              <Typography size="small" color="D-100">
                Unsaved changes
              </Typography>
            )}
            <Button
              variant="filled"
              color="neutral"
              size="medium"
              onClick={() => p.dispatch('Save')}
              loading={p.isSaving}
              disabled={p.isSaving}
            >
              Save changes
            </Button>
          </div>
        </div>
      </div>

      <div className={styles.pageWrapper}>
        <div className={styles.mainContent}>
          <div className={styles.sectionsContainer}>
            <ProfileSection
              icon={<FontAwesomeIcon icon={faUser} />}
              title="Identity"
              description="Key details that define you"
              completion={completions.identity}
              iconBg={rgb('P-20')}
              iconColor={rgb('P-100')}
            >
              <IdentitySection vm={p} />
            </ProfileSection>

            <ProfileSection
              icon={<FontAwesomeIcon icon={faFileLines} />}
              title="Your Story"
              description="Bio, accomplishments and social links"
              completion={completions.story}
              iconBg={rgb('G-40')}
              iconColor={rgb('SUCCESS-100')}
            >
              <StorySection vm={p} />
            </ProfileSection>

            <ProfileSection
              icon={<FontAwesomeIcon icon={faImage} />}
              title="Media"
              description="Profile banner & intro video"
              completion={completions.media}
              iconBg={rgb('D-40')}
              iconColor={rgb('D-100')}
            >
              <MediaSection vm={p} />
            </ProfileSection>

            <ProfileSection
              icon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
              title="Discovery"
              description="Help your audience find you. Pick up to 5 tags"
              completion={completions.discovery}
              iconBg={rgb('B-light')}
              iconColor={rgb('B-standard')}
            >
              <DiscoverySection vm={p} />
            </ProfileSection>
          </div>
        </div>

        <div className={styles.sidebar}>
          <div className={styles.sidebarSticky}>
            <div>
              <Typography variant="title" size="medium">
                Consumer app preview
              </Typography>
              <Typography size="small" color="N-60">
                This is how your customers will see your channel
              </Typography>
            </div>

            <div className={styles.previewWrapper}>
              <Preview profile={debouncedProfile} />
            </div>

            <div className={styles.appUrlSection}>
              <Input
                label="App URL"
                name="customURL"
                value={p.customUrl.value}
                onChange={(e) => p.customUrl.onChange(e.target.value)}
                startCta={
                  <span className={styles.urlPrefix}>my.playbookapp.io/</span>
                }
                endCta={
                  <button
                    type="button"
                    className={styles.copyButton}
                    onClick={handleCopyUrl}
                    aria-label="Copy URL"
                  >
                    <FontAwesomeIcon icon={faCopy} />
                  </button>
                }
                disabled={!p.customUrl.isEditable}
              />
              <Typography size="small" color="N-60">
                This text is editable until your profile is live. After that,
                you will be unable to make changes.
              </Typography>
            </div>
          </div>
        </div>
      </div>

      <AiAssistModal vm={aiAssist} onAccept={handleAiAccept} />
    </div>
  );
};
